import { useMutation, useQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { PAPEIS } from "@vetbolso/contracts";
import { MASCARA_CRMV, MASCARA_UF, PERFIS_INFO } from "@vetbolso/core";
import { mascarar } from "@vetbolso/ui";
import { useState } from "react";
import { api, ler, queryClient } from "../api.ts";
import { useEu } from "../router.tsx";
import {
  Aviso,
  Botao,
  Cabecalho,
  Campo,
  Carregando,
  Cartao,
  Dialogo,
  dataHora,
  Entrada,
  ErroCarga,
  Selecao,
  Selo,
  Tabela,
  Vazio,
} from "../ui.tsx";

const msg = (e: unknown) => (e instanceof Error ? e.message : String(e));
const nomePerfil = (p: string | null) => (p ? (PERFIS_INFO.find((x) => x.id === p)?.nome ?? p) : "—");

function NovoMembro({ aberto, aoFechar }: { aberto: boolean; aoFechar: () => void }) {
  const criar = useMutation({
    mutationFn: (b: { email: string; nome: string; papel: (typeof PAPEIS)[number]; senha: string; crmv?: string; uf?: string }) =>
      ler(api.usuarios.$post({ json: b })),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["usuarios"] });
      aoFechar();
    },
  });
  return (
    <Dialogo aberto={aberto} aoFechar={aoFechar} titulo="Novo membro da equipe">
      <form
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          const f = new FormData(e.currentTarget);
          const s = (k: string) => String(f.get(k) ?? "").trim() || undefined;
          criar.mutate({
            email: s("email")!,
            nome: s("nome")!,
            papel: s("papel") as (typeof PAPEIS)[number],
            senha: String(f.get("senha")),
            crmv: s("crmv"),
            uf: s("uf")?.toUpperCase(),
          });
        }}
      >
        {criar.error ? <Aviso>{msg(criar.error)}</Aviso> : null}
        <div className="grid gap-3 sm:grid-cols-2">
          <Campo rotulo="Nome">
            <Entrada name="nome" required minLength={2} />
          </Campo>
          <Campo rotulo="E-mail">
            <Entrada name="email" type="email" required />
          </Campo>
          <Campo rotulo="Papel" dica="Editor escreve; revisor (com CRMV) aprova e publica; admin faz tudo.">
            <Selecao name="papel" defaultValue="editor">
              {PAPEIS.map((p) => (
                <option key={p}>{p}</option>
              ))}
            </Selecao>
          </Campo>
          <Campo rotulo="Senha inicial" dica="Ao menos 10 caracteres; repasse por canal seguro.">
            <Entrada name="senha" type="password" autoComplete="new-password" required minLength={10} />
          </Campo>
          <Campo rotulo="CRMV" dica="Obrigatório para aprovar fichas.">
            <Entrada name="crmv" inputMode="numeric" pattern="\d{3,6}" title="3 a 6 dígitos" onChange={(e) => mascarar(e, MASCARA_CRMV)} />
          </Campo>
          <Campo rotulo="UF do CRMV">
            <Entrada
              name="uf"
              maxLength={2}
              pattern="[A-Z]{2}"
              title="Sigla da UF, ex.: SP"
              placeholder="SP"
              onChange={(e) => mascarar(e, MASCARA_UF)}
            />
          </Campo>
        </div>
        <div className="flex justify-end gap-2">
          <Botao onClick={aoFechar}>Cancelar</Botao>
          <Botao type="submit" variante="primario" disabled={criar.isPending}>
            Criar acesso
          </Botao>
        </div>
      </form>
    </Dialogo>
  );
}

export function PaginaUsuarios() {
  const [q, setQ] = useState("");
  const [equipe, setEquipe] = useState<"" | "1" | "0">("");
  const [novo, setNovo] = useState(false);
  const { data, error } = useQuery({
    queryKey: ["usuarios", q, equipe],
    queryFn: () => ler(api.usuarios.$get({ query: { q: q || undefined, equipe: equipe || undefined } })),
  });
  return (
    <>
      <Cabecalho
        titulo="Usuários"
        sub="Assinantes do app e equipe editorial."
        acoes={
          <Botao variante="primario" onClick={() => setNovo(true)}>
            Novo membro da equipe
          </Botao>
        }
      />
      <div className="flex flex-wrap gap-2">
        <Entrada
          className="w-64"
          type="search"
          placeholder="Buscar por nome ou e-mail"
          onKeyDown={(e) => e.key === "Enter" && setQ(e.currentTarget.value.trim())}
          aria-label="Buscar"
        />
        <Selecao className="w-auto" value={equipe} onChange={(e) => setEquipe(e.target.value as "" | "1" | "0")} aria-label="Tipo">
          <option value="">Todos</option>
          <option value="0">Usuários do app</option>
          <option value="1">Equipe</option>
        </Selecao>
      </div>
      {error ? (
        <ErroCarga erro={error} />
      ) : !data ? (
        <Carregando />
      ) : !data.length ? (
        <Vazio>Ninguém encontrado.</Vazio>
      ) : (
        <Tabela>
          <thead>
            <tr>
              <th>Pessoa</th>
              <th>Papel</th>
              <th>Perfil</th>
              <th>Acesso</th>
              <th>Desde</th>
            </tr>
          </thead>
          <tbody>
            {data.map((u) => (
              <tr key={u.id} className="hover:bg-surface-2">
                <td>
                  <Link to="/usuarios/$id" params={{ id: u.id }} className="font-semibold text-ink hover:text-primary">
                    {u.nome || u.email}
                  </Link>
                  <div className="text-xs text-muted">{u.email}</div>
                </td>
                <td className="text-xs">{u.papel}</td>
                <td className="text-xs text-muted">{nomePerfil(u.perfil)}</td>
                <td>
                  <div className="flex flex-wrap gap-1">
                    {u.banido ? <Selo tom="perigo">suspenso</Selo> : null}
                    {u.concessao === true ? (
                      <Selo tom="destaque">concessão</Selo>
                    ) : u.concessao === false ? (
                      <Selo tom="alerta">revogado</Selo>
                    ) : null}
                    {u.compraAtiva ? <Selo tom="ok">compra ativa</Selo> : null}
                    {u.concessaoMapas === true ? (
                      <Selo tom="destaque">mapas concedidos</Selo>
                    ) : u.concessaoMapas === false ? (
                      <Selo tom="alerta">mapas revogados</Selo>
                    ) : null}
                    {!u.temCpf && u.papel === "usuario" ? <Selo>sem CPF</Selo> : null}
                  </div>
                </td>
                <td className="whitespace-nowrap text-xs text-muted">{new Date(u.criadoEm).toLocaleDateString("pt-BR")}</td>
              </tr>
            ))}
          </tbody>
        </Tabela>
      )}
      <NovoMembro aberto={novo} aoFechar={() => setNovo(false)} />
    </>
  );
}

export function PaginaUsuario() {
  const { id } = useParams({ from: "/area/usuarios/$id" });
  const eu = useEu();
  const { data, error } = useQuery({ queryKey: ["usuario", id], queryFn: () => ler(api.usuarios[":id"].$get({ param: { id } })) });
  const atualizar = () => {
    queryClient.invalidateQueries({ queryKey: ["usuario", id] });
    queryClient.invalidateQueries({ queryKey: ["usuarios"] });
  };
  const editar = useMutation({
    mutationFn: (b: { papel?: "admin" | "editor" | "revisor" | "usuario"; banido?: boolean; crmv?: string | null; uf?: string | null }) =>
      ler(api.usuarios[":id"].$patch({ param: { id }, json: b })),
    onSuccess: atualizar,
  });
  const conceder = useMutation({
    mutationFn: (b: { ativo: boolean; plano: "padrao" | "estudante"; motivo: string }) =>
      ler(api.usuarios[":id"].concessao.$put({ param: { id }, json: b })),
    onSuccess: atualizar,
  });
  const remover = useMutation({ mutationFn: () => ler(api.usuarios[":id"].concessao.$delete({ param: { id } })), onSuccess: atualizar });
  const concederMapas = useMutation({
    mutationFn: (b: { ativo: boolean; motivo: string }) => ler(api.usuarios[":id"].mapas.$put({ param: { id }, json: b })),
    onSuccess: atualizar,
  });
  const removerMapas = useMutation({ mutationFn: () => ler(api.usuarios[":id"].mapas.$delete({ param: { id } })), onSuccess: atualizar });

  if (error) return <Aviso>{msg(error)}</Aviso>;
  if (!data) return <Carregando />;
  const { usuario: u, acesso, concessao, concessaoMapas, compras } = data;
  const eMesmo = u.id === eu?.id;
  const erro = editar.error ?? conceder.error ?? remover.error ?? concederMapas.error ?? removerMapas.error;

  return (
    <>
      <Cabecalho
        titulo={u.nome || u.email}
        sub={
          <span className="flex flex-wrap items-center gap-2">
            <Link to="/usuarios" className="hover:text-primary">
              Usuários
            </Link>
            {u.email} · desde {new Date(u.criadoEm).toLocaleDateString("pt-BR")}
            {u.banido ? <Selo tom="perigo">suspenso</Selo> : null}
          </span>
        }
      />
      {erro ? <Aviso>{msg(erro)}</Aviso> : null}
      <div className="grid gap-4 lg:grid-cols-2">
        <Cartao titulo="Acesso ao conteúdo">
          <div className="flex items-center gap-2">
            <Selo tom={acesso.ativo ? "ok" : "neutro"}>{acesso.ativo ? "liberado" : "sem acesso"}</Selo>
            {acesso.plano ? <Selo tom="destaque">{acesso.plano}</Selo> : null}
            <span className="text-xs text-muted">{acesso.motivo}</span>
          </div>
          {acesso.expiraEm ? <p className="text-xs text-muted">Expira em {dataHora(acesso.expiraEm)}</p> : null}
          <form
            className="flex flex-col gap-2 border-t border-line pt-3"
            onSubmit={(e) => {
              e.preventDefault();
              const f = new FormData(e.currentTarget);
              conceder.mutate({
                ativo: f.get("ativo") === "1",
                plano: f.get("plano") as "padrao" | "estudante",
                motivo: String(f.get("motivo")).trim(),
              });
            }}
          >
            <p className="text-xs text-muted">
              Concessão manual vence a compra, inclusive para revogar. Fica registrada no log com o motivo.
            </p>
            <div className="grid gap-2 sm:grid-cols-[auto_auto_1fr]">
              <Selecao name="ativo" defaultValue={concessao?.ativo === false ? "0" : "1"} aria-label="Concessão">
                <option value="1">Conceder</option>
                <option value="0">Revogar</option>
              </Selecao>
              <Selecao name="plano" defaultValue={concessao?.plano ?? "padrao"} aria-label="Plano">
                <option value="padrao">padrão</option>
                <option value="estudante">estudante</option>
              </Selecao>
              <Entrada
                name="motivo"
                placeholder="Motivo (ex.: cortesia parceiro, estorno)"
                defaultValue={concessao?.motivo ?? ""}
                required
                minLength={3}
              />
            </div>
            <div className="flex gap-2">
              <Botao type="submit" variante="primario" disabled={conceder.isPending}>
                Aplicar
              </Botao>
              {concessao ? (
                <Botao variante="fantasma" disabled={remover.isPending} onClick={() => remover.mutate()}>
                  Remover concessão (volta a valer a compra)
                </Botao>
              ) : null}
            </div>
          </form>
        </Cartao>

        <Cartao titulo="Módulo de mapas mentais">
          <div className="flex items-center gap-2">
            <Selo tom={acesso.mapas ? "ok" : "neutro"}>{acesso.mapas ? "liberado" : "bloqueado"}</Selo>
            {concessaoMapas ? (
              <span className="text-xs text-muted">{concessaoMapas.ativo ? "concedido" : "revogado"} pela equipe</span>
            ) : null}
          </div>
          <form
            className="flex flex-col gap-2 border-t border-line pt-3"
            onSubmit={(e) => {
              e.preventDefault();
              const f = new FormData(e.currentTarget);
              concederMapas.mutate({ ativo: f.get("ativo") === "1", motivo: String(f.get("motivo")).trim() });
            }}
          >
            <p className="text-xs text-muted">
              Vence a compra do adicional, inclusive para revogar, e não muda o acesso ao app. Só tem efeito para quem tem acesso ao app.
              Fica registrada no log com o motivo.
            </p>
            <div className="grid gap-2 sm:grid-cols-[auto_1fr]">
              <Selecao name="ativo" defaultValue={concessaoMapas?.ativo === false ? "0" : "1"} aria-label="Concessão dos mapas">
                <option value="1">Conceder</option>
                <option value="0">Revogar</option>
              </Selecao>
              <Entrada
                name="motivo"
                placeholder="Motivo (ex.: comprou o ebook, cortesia)"
                defaultValue={concessaoMapas?.motivo ?? ""}
                required
                minLength={3}
              />
            </div>
            <div className="flex gap-2">
              <Botao type="submit" variante="primario" disabled={concederMapas.isPending}>
                Aplicar
              </Botao>
              {concessaoMapas ? (
                <Botao variante="fantasma" disabled={removerMapas.isPending} onClick={() => removerMapas.mutate()}>
                  Remover concessão (volta a valer a compra)
                </Botao>
              ) : null}
            </div>
          </form>
        </Cartao>

        <Cartao titulo="Conta">
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
            <dt className="text-muted">Perfil no app</dt>
            <dd>{nomePerfil(u.perfil)}</dd>
            <dt className="text-muted">CPF</dt>
            <dd>{u.temCpf ? "informado (guardado só como hash)" : "não informado"}</dd>
          </dl>
          <form
            className="flex flex-col gap-2 border-t border-line pt-3"
            onSubmit={(e) => {
              e.preventDefault();
              const f = new FormData(e.currentTarget);
              const crmv = String(f.get("crmv")).trim();
              const uf = String(f.get("uf")).trim().toUpperCase();
              editar.mutate({ papel: f.get("papel") as "usuario", crmv: crmv || null, uf: uf || null });
            }}
          >
            <div className="grid gap-2 sm:grid-cols-3">
              <Campo rotulo="Papel">
                <Selecao name="papel" defaultValue={u.papel} disabled={eMesmo}>
                  <option value="usuario">usuario</option>
                  {PAPEIS.map((p) => (
                    <option key={p}>{p}</option>
                  ))}
                </Selecao>
              </Campo>
              <Campo rotulo="CRMV">
                <Entrada
                  name="crmv"
                  inputMode="numeric"
                  pattern="\d{3,6}"
                  title="3 a 6 dígitos"
                  defaultValue={u.crmv ?? ""}
                  onChange={(e) => mascarar(e, MASCARA_CRMV)}
                />
              </Campo>
              <Campo rotulo="UF">
                <Entrada
                  name="uf"
                  maxLength={2}
                  pattern="[A-Z]{2}"
                  title="Sigla da UF, ex.: SP"
                  placeholder="SP"
                  defaultValue={u.uf?.toUpperCase() ?? ""}
                  onChange={(e) => mascarar(e, MASCARA_UF)}
                />
              </Campo>
            </div>
            <div className="flex flex-wrap gap-2">
              <Botao type="submit" disabled={editar.isPending}>
                Salvar conta
              </Botao>
              {!eMesmo ? (
                <Botao
                  variante={u.banido ? "secundario" : "perigo"}
                  disabled={editar.isPending}
                  onClick={() => editar.mutate({ banido: !u.banido })}
                >
                  {u.banido ? "Reativar" : "Suspender e encerrar sessões"}
                </Botao>
              ) : null}
            </div>
          </form>
        </Cartao>
      </div>

      <Cartao titulo={`Compras vinculadas ao CPF (${compras.length})`}>
        {compras.length ? (
          <Tabela>
            <thead>
              <tr>
                <th>Data</th>
                <th>Provedor</th>
                <th>Produto</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {compras.map((c) => (
                <tr key={c.id}>
                  <td className="text-xs">{dataHora(c.criadoEm)}</td>
                  <td className="text-xs">
                    {c.provedor} <span className="font-mono text-muted">{c.pedidoId}</span>
                  </td>
                  <td className="text-xs">
                    {c.produtoNome ?? "—"} {c.plano === "padrao" ? null : <Selo tom="destaque">{c.plano}</Selo>}
                  </td>
                  <td>
                    <Selo tom={c.ativo ? "ok" : "neutro"}>{c.ativo ? "ativa" : c.status}</Selo>
                  </td>
                </tr>
              ))}
            </tbody>
          </Tabela>
        ) : (
          <p className="text-sm text-muted">{u.temCpf ? "Nenhuma compra com este CPF." : "Sem CPF, não há como vincular compras."}</p>
        )}
      </Cartao>
    </>
  );
}
